import React, { useState, Fragment, useContext } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  List,
  Grid,
  Button,
  ListItem,
  Typography,
  ListItemText,
  Divider,
} from '@material-ui/core';

import i18n from 'i18n-js';
import Can from 'components/Can';
import { useConfirm } from 'hooks';
import { Card } from 'components/UI';
import { BastionContext } from 'context';
import { locales } from 'config/locales';
import { OrganizationsActions } from 'state/modules/organizations';
import { useStyles, listStyles, customCardStyles } from './OrganizationDetails.styled';
import { UploadOrgSettingLogoModal, UpdateOrgSettingsInfoModal } from 'components/Modals';

export const OrganizationDetails = ({ organization, editable }) => {
  const classes = useStyles();
  const cardClasses = customCardStyles();
  const itemsClasses = listStyles();
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openEditInfoModal, setOpenEditInfoModal] = useState(false);
  const dispatch = useDispatch();

  const id = organization?.id;

  const [toggleDeleteFile, DeleteFileConfirm] = useConfirm({
    title: i18n.t('documents.delete_file'),
    message: i18n.t('documents.delete_file_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => {
      dispatch(OrganizationsActions.deleteOrganizationLogo(id));
    },
  });

  const { theme } = useContext(BastionContext);

  /**
   * handle upload logo modal
   */
  const handleUploadLogoModal = () => {
    setOpenUploadModal((prevState) => !prevState);
  };

  /**
   * handle edit info modal
   */
  const handleEditInfoModal = () => {
    setOpenEditInfoModal((prevState) => !prevState);
  };

  const getBasicInfo = (organization = {}) => {
    const data = [
      { label: i18n.t('organization.name'), value: organization?.name },
      { label: i18n.t('organization.email'), value: organization?.email },
      { label: i18n.t('organization.phone'), value: organization?.phone_number },
      { label: i18n.t('organization.description'), value: organization?.description },
    ];
    return data;
  };

  const getContactInfo = (organization = {}) => {
    const data = [
      { label: i18n.t('organization.address'), value: organization?.address },
      { label: i18n.t('organization.state'), value: organization?.state },
    ];
    data.hasInfo = !!(data[0].value || data[1].value);
    return data;
  };

  const getTimeZoneInfo = (organization = {}) => {
    const locale = locales?.find((l) => l?.locale === organization?.locale);
    const data = [
      { label: i18n.t('organization.time_zone'), value: organization?.time_zone },
      { label: i18n.t('organization.locale'), value: locale?.label || organization?.locale },
    ];
    data.hasInfo = !!(data[0].value || data[1].value);
    return data;
  };

  const basicInfo = getBasicInfo(organization);
  const contactInfo = getContactInfo(organization);
  const timeZoneInfo = getTimeZoneInfo(organization);

  return (
    <Card
      title={editable ? i18n.t('organization.details') : i18n.t('common.details')}
      contentRootClasses={cardClasses}
    >
      <DeleteFileConfirm />
      <Box className={classes.cardContentPadding}>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="h3">
              <Box
                fontSize={13}
                fontWeight="bold"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                {editable ? i18n.t('organization.logo') : i18n.t('common.logo')}

                {editable ? (
                  <Can I="update" a="organization_config">
                    <div className={classes.logoActions}>
                      <Button
                        className={classes.updateButton}
                        onClick={handleUploadLogoModal}
                        color="primary"
                        data-cy="update-logo-btn"
                      >
                        {i18n.t('organization.update_logo')}
                      </Button>
                      {organization?.logo_url ? (
                        <Button
                          className={classes.updateButton}
                          onClick={toggleDeleteFile}
                          color="primary"
                          data-cy="delete-logo-btn"
                        >
                          {i18n.t('organization.delete_logo')}
                        </Button>
                      ) : null}
                    </div>
                  </Can>
                ) : null}
              </Box>
            </Typography>
          </Grid>

          <Box className={classes.imagePlaceHolder}>
            <img
              className={classes.image}
              style={{
                objectFit: organization?.logo_url ? 'contain' : 'scale-down',
              }}
              src={organization?.logo_url || theme.logo.src}
              alt={organization?.logo_url ? 'organization-logo' : 'theme-logo'}
              data-cy="logo-wrapper"
            />
          </Box>

          <Divider className={classes.detaileDivider} light={true} />

          <Grid item xs={12}>
            <Typography variant="h3">
              <Box
                fontSize={13}
                fontWeight="bold"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                {i18n.t('reservations.basic_information')}

                {editable ? (
                  <Can I="update" a="organization_config">
                    <Button
                      className={classes.updateButton}
                      onClick={handleEditInfoModal}
                      color="primary"
                    >
                      {i18n.t('organization.update_info')}
                    </Button>
                  </Can>
                ) : null}
              </Box>
            </Typography>
          </Grid>

          <List className={classes.infoList}>
            {basicInfo.map(
              (val, index) =>
                val.value && (
                  <Fragment key={index}>
                    <ListItem className={classes.infoItem}>
                      <ListItemText
                        classes={itemsClasses}
                        primary={`${val.label}:`}
                        secondary={val.value}
                      />
                    </ListItem>
                  </Fragment>
                )
            )}
          </List>

          {contactInfo.hasInfo ? (
            <>
              <Divider className={classes.detaileDivider} light={true} />
              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box fontSize={13} fontWeight="bold">
                    {i18n.t('organization.contact_info')}
                  </Box>
                </Typography>
              </Grid>
              <List className={classes.infoList}>
                {contactInfo.map(
                  (val, index) =>
                    val.value && (
                      <Fragment key={index}>
                        <ListItem className={classes.infoItem}>
                          <ListItemText
                            classes={itemsClasses}
                            primary={`${val.label}:`}
                            secondary={val.value}
                          />
                        </ListItem>
                      </Fragment>
                    )
                )}
              </List>{' '}
            </>
          ) : null}

          {timeZoneInfo.hasInfo ? (
            <>
              <Divider className={classes.detaileDivider} light={true} />

              <Grid item xs={12}>
                <Typography variant="h3">
                  <Box fontSize={13} fontWeight="bold">
                    {i18n.t('organization.time_zone')}
                  </Box>
                </Typography>
              </Grid>

              <List className={classes.infoList}>
                {timeZoneInfo.map(
                  (val, index) =>
                    val.value && (
                      <Fragment key={index}>
                        <ListItem className={classes.infoItem}>
                          <ListItemText
                            classes={itemsClasses}
                            primary={`${val.label}:`}
                            secondary={val.value}
                          />
                        </ListItem>
                      </Fragment>
                    )
                )}
              </List>
            </>
          ) : null}
        </Grid>
      </Box>
      {editable && openUploadModal && (
        <UploadOrgSettingLogoModal
          id={id}
          open={openUploadModal}
          handleClick={handleUploadLogoModal}
        />
      )}

      {editable && openEditInfoModal && (
        <UpdateOrgSettingsInfoModal
          id={id}
          open={openEditInfoModal}
          handleClick={handleEditInfoModal}
        />
      )}
    </Card>
  );
};
